import {
  Component,
  DoCheck,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { Overlay, OverlayRef, ConnectedPosition } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { SitesService } from "../sites.service";

// Sites: block-and-confirm clinic switch prompt (see SitesService.requestOpen).
//
// Rendered once per page (patients/patexams both drop in <app-site-switch-prompt>)
// and reads straight off SitesService.pendingSwitch — no inputs/outputs needed
// since it's the same singleton service everywhere.
//
// Anchoring: pendingSwitch carries the HTMLElement the user actually clicked
// (captured at the call site and threaded through SitesService.requestOpen).
// We use CDK's Overlay service to float a small card next to *that* element,
// via flexibleConnectedTo — CDK overlays attach to a single container appended
// to <body>, escaping whatever transform/overflow/stacking-context an ancestor
// might apply. That's a deliberate fix for the old position:fixed banner (once
// in docpanel.component.html), which broke visibility exactly because it was
// nested deep in a component tree subject to ancestor CSS. If no anchor is
// available for some reason, we fall back to a centered overlay near the top
// of the viewport, so the prompt is always visible either way.
@Component({
  selector: "app-site-switch-prompt",
  templateUrl: "./site-switch-prompt.component.html",
  styleUrls: ["./site-switch-prompt.component.scss"],
  standalone: false,
})
export class SiteSwitchPromptComponent implements DoCheck, OnDestroy {
  @ViewChild("promptTemplate", { static: true }) promptTemplate: TemplateRef<any>;

  private overlayRef: OverlayRef | null = null;
  // Tracks pendingSwitch by reference — requestOpen() always creates a fresh
  // object, so identity change is exactly "a new prompt should open (or the
  // old one closed)".
  private lastPending: unknown = null;

  private static readonly ANCHORED_POSITIONS: ConnectedPosition[] = [
    { originX: "end", originY: "bottom", overlayX: "end", overlayY: "top", offsetY: 8 },
    { originX: "end", originY: "top", overlayX: "end", overlayY: "bottom", offsetY: -8 },
    { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 8 },
    { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -8 },
  ];

  constructor(
    public sites: SitesService,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
  ) {}

  ngDoCheck(): void {
    const pending = this.sites.pendingSwitch;
    if (pending !== this.lastPending) {
      this.lastPending = pending;
      if (pending) this.open(pending.anchor);
      else this.close();
    }
  }

  private open(anchor?: HTMLElement | null): void {
    this.close();
    const positionStrategy =
      anchor && anchor.isConnected
        ? this.overlay
            .position()
            .flexibleConnectedTo(anchor)
            .withPositions(SiteSwitchPromptComponent.ANCHORED_POSITIONS)
            .withPush(true)
            .withViewportMargin(12)
        : this.overlay.position().global().centerHorizontally().top("12%");

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: "site-switch-backdrop",
      panelClass: "site-switch-overlay-pane",
    });
    this.overlayRef.backdropClick().subscribe(() => this.sites.cancelSwitch());
    this.overlayRef.attach(new TemplatePortal(this.promptTemplate, this.vcr));
  }

  private close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy(): void {
    this.close();
  }
}
